import { useEffect, useState } from "react";
import { Button, Card, Typography } from "@arco-design/web-react";

import {
  fetchDatabaseConnectivity,
  fetchHeartbeat,
  type DatabaseConnectivityResult,
  type DatabaseType,
} from "../api/sqlServerDemoApi";
import { runWithSpan } from "../../../lib/observability/otel";

const { Title, Paragraph, Text } = Typography;

/**
 * 数据库连通性 Demo 页面：演示前后端可观测性与连通性查询。
 */
export function DatabaseConnectivityDemoPage() {
  const [databaseType, setDatabaseType] = useState<DatabaseType>("sqlserver");
  const [customerId, setCustomerId] = useState("CUST-001");
  const [payload, setPayload] = useState<DatabaseConnectivityResult | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [heartbeatLoading, setHeartbeatLoading] = useState(false);
  const [heartbeatLatencyMs, setHeartbeatLatencyMs] = useState<number | null>(null);
  const [heartbeatErrorMessage, setHeartbeatErrorMessage] = useState("");
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 960 : false,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsCompact(window.innerWidth < 960);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /** 触发数据库连通性查询。 */
  const handleCheckConnectivity = async (): Promise<void> => {
    setLoading(true);
    setErrorMessage("");
    try {
      const result = await runWithSpan(
        `frontend.demo.${databaseType}.connectivity`,
        () => fetchDatabaseConnectivity(databaseType, customerId),
        { "demo.customer_id": customerId, "demo.database_type": databaseType },
      );
      setPayload(result);
    } catch (error) {
      const fallbackMessage = "请求失败，请检查后端服务与数据库连接。";
      const message = error instanceof Error ? error.message : fallbackMessage;
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  /** 触发心跳接口请求并测量接口往返耗时。 */
  const handleHeartbeat = async (): Promise<void> => {
    setHeartbeatLoading(true);
    setHeartbeatErrorMessage("");
    try {
      const startedAt = performance.now();
      await runWithSpan(
        "frontend.demo.healthz.heartbeat",
        () => fetchHeartbeat(),
        { "demo.endpoint": "/healthz" },
      );
      const latency = Number((performance.now() - startedAt).toFixed(2));
      setHeartbeatLatencyMs(latency);
    } catch (error) {
      const fallbackMessage = "心跳请求失败，请检查后端服务。";
      const message = error instanceof Error ? error.message : fallbackMessage;
      setHeartbeatErrorMessage(message);
    } finally {
      setHeartbeatLoading(false);
    }
  };

  return (
    <main style={{ display: "grid", gap: 20 }}>
      <Card
        bodyStyle={{ padding: 28 }}
        style={{
          border: "1px solid #e5e6eb",
          borderRadius: 20,
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <Title heading={4} style={{ margin: "0 0 8px", color: "#1d2129" }}>
              数据库连通性 Demo
            </Title>
            <Paragraph style={{ margin: 0, color: "#4e5969", fontSize: 15 }}>
              该页面通过统一 <code>httpClient</code> 调用后端接口，贯穿 trace/session 并创建
              OpenTelemetry Span。
            </Paragraph>
          </div>
          <Button
            type="secondary"
            disabled={heartbeatLoading}
            onClick={() => void handleHeartbeat()}
          >
            {heartbeatLoading ? "测速中..." : "心跳测速"}
          </Button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr auto",
            gap: 14,
            marginTop: 20,
          }}
        >
          <label style={{ display: "grid", gap: 6 }}>
            <Text style={{ color: "#4e5969", fontWeight: 600 }}>数据库类型</Text>
            <select
              aria-label="数据库类型"
              value={databaseType}
              onChange={(event) => setDatabaseType(event.target.value as DatabaseType)}
              style={{
                height: 42,
                border: "1px solid #d9d9d9",
                borderRadius: 12,
                padding: "0 12px",
                background: "#ffffff",
              }}
            >
              <option value="sqlserver">SQL Server</option>
              <option value="mysql">MySQL</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 6 }}>
            <Text style={{ color: "#4e5969", fontWeight: 600 }}>客户编号</Text>
            <input
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
              placeholder="例如 CUST-001"
              style={{
                height: 42,
                border: "1px solid #d9d9d9",
                borderRadius: 12,
                padding: "0 12px",
                background: "#ffffff",
              }}
            />
          </label>
          <Button
            type="primary"
            disabled={loading || customerId.trim().length === 0}
            onClick={() => void handleCheckConnectivity()}
            style={{
              alignSelf: "end",
              height: 42,
            }}
          >
            {loading ? "查询中..." : "查询连通性"}
          </Button>
        </div>

        {heartbeatLatencyMs !== null ? (
          <div
            style={{
              marginTop: 16,
              borderRadius: 14,
              background: "#f2f9f3",
              padding: "12px 14px",
              color: "#00b42a",
              fontWeight: 600,
            }}
          >
            心跳耗时：{heartbeatLatencyMs} ms
          </div>
        ) : null}

        {heartbeatErrorMessage ? (
          <div
            style={{
              marginTop: 16,
              borderRadius: 14,
              background: "#fff2f0",
              padding: "12px 14px",
              color: "#f53f3f",
              fontWeight: 600,
            }}
          >
            {heartbeatErrorMessage}
          </div>
        ) : null}

        {errorMessage ? (
          <div
            style={{
              marginTop: 16,
              borderRadius: 14,
              background: "#fff2f0",
              padding: "12px 14px",
              color: "#f53f3f",
              fontWeight: 600,
            }}
          >
            {errorMessage}
          </div>
        ) : null}
      </Card>

      {payload ? (
        <Card
          bodyStyle={{ padding: 24 }}
          style={{
            border: "1px solid #e5e6eb",
            borderRadius: 20,
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Title heading={5} style={{ marginTop: 0 }}>
            返回结果
          </Title>
          <pre
            style={{
              margin: 0,
              borderRadius: 16,
              background: "#0f172a",
              color: "#e2e8f0",
              padding: 18,
              overflowX: "auto",
            }}
          >
            {JSON.stringify(payload, null, 2)}
          </pre>
        </Card>
      ) : null}
    </main>
  );
}

/** 兼容既有引用的导出名称。 */
export const SqlServerDemoPage = DatabaseConnectivityDemoPage;
